import { FC } from "react";
import AppStoreQRCode from "../assets/app-store-qr-code.svg";
import ExternalLinkIcon from "../assets/li_external-link.svg";
import { useIsMobile } from "../utils/useIsMobile";
import { useToast } from "./Toast";

const PLATFORM_LINK = import.meta.env.VITE_PLATFORM_URL;
const APP_ONE_LINK = import.meta.env.VITE_APP_ONE_LINK;
const APP_STORE_LINK = import.meta.env.VITE_APP_STORE_LINK;
const PLAY_STORE_LINK = import.meta.env.VITE_PLAY_STORE_LINK;
const FALLBACK_HREF = `${APP_STORE_LINK} OR ${PLAY_STORE_LINK}`;

type Props = {
  embedded: boolean;
  context?: "Live" | "Demo";
};


const ThankYouContent: FC<Props> = ({ embedded, context = "Live" }) => {
  const isMobile = useIsMobile();
  const { showToast, ToastComponent } = useToast();
  const isLive = context === "Live";

  const handleCopyLink = () => {
    try {
      navigator.clipboard.writeText(PLATFORM_LINK);
      showToast("Link copied to clipboard");
    } catch (e) {
      showToast("Could not copy link");
      console.log(e)
    }
  };

  return (
    <div>
      {ToastComponent}

      {isLive && (
        <div className="w-full mb-6">
          <p className="text-base leading-6 text-content-secondary">
            We're reviewing your application. You'll receive an email shortly with the next steps.
          </p>
        </div>
      )}

      {!embedded && isLive && (
        <div className="w-full mb-6">
          <p className="text-base leading-6 text-content-secondary">
            <span className="font-bold">Your demo account is ready</span>
            <br />
            Log in to practise trading, explore the platform, and track your application status.
          </p>
        </div>
      )}

      {!embedded && !isLive && (
        <div className="w-full mb-6">
          <p className="text-base leading-6 text-content-secondary">
            Log in to test your trading strategies and explore the platform.
          </p>
        </div>
      )}

      {!embedded && (
        isMobile ? (
          <div className="w-full flex items-center py-6">
            <button
              onClick={handleCopyLink}
              className="text-blue-400 inline-flex items-center gap-1 cursor-pointer"
            >
              Copy link
              <img src={ExternalLinkIcon} alt="" className="inline-block w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="w-full flex flex-col gap-2 py-6">
            <div className="bg-surface-separator flex gap-6 items-center p-3 w-full">
              <a
                id="qr-code"
                href={APP_ONE_LINK}
                data-fallback-href={FALLBACK_HREF}
                data-onelink-href={APP_ONE_LINK}
                className="w-[55px] h-[55px] shrink-0 bg-white"
              >
                <img src={AppStoreQRCode} alt="Download app QR code" className="w-full h-full" />
              </a>
              <div className="flex-1">
                <p className="text-sm leading-5 text-content-secondary">
                  Prefer to trade on the go?
                  <br />
                  Scan QR code to get our app
                </p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-xs leading-4 text-content-secondary">
                Available on&nbsp;
                <a
                  href={APP_ONE_LINK}
                  data-fallback-href={APP_STORE_LINK}
                  data-onelink-href={APP_ONE_LINK}
                  className="text-blue-400 underline"
                >
                  App Store
                </a>
                <span className="text-blue-400 text-[10px] align-top pl-0.5">®</span>
                &nbsp;and&nbsp;
                <a
                  href={APP_ONE_LINK}
                  data-fallback-href={PLAY_STORE_LINK}
                  data-onelink-href={APP_ONE_LINK}
                  className="text-blue-400 underline"
                >
                  Google Play
                </a>
              </p>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default ThankYouContent;
